import * as React from "react";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={
      <ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem", color: "#fff" }} />
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "#39484E",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

export default function Selection() {
  const [expanded, setExpanded] = React.useState("");

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  return (
    <Box margin={{ xs: "25% 0%", sm: "10% 0%" }}>
      <Typography
        variant={"h3"}
        fontWeight={700}
        textAlign={"left"}
        color={"#39484E"}
        paddingBottom={"2%"}
      >
        Our Selection Process
      </Typography>
      <Grid container>
        <Grid item xs={12} sm={6}>
          <Typography
            variant={"h6"}
            fontWeight={400}
            textAlign={"left"}
            color={"#000"}
            paddingBottom={{ xs: "10%", sm: "2%" }}
            paddingRight={{ xs: "0%", sm: "4%" }}
          >
            Our objective is to enable innovators within African countries to
            secure the mix of resources for the execution of ideas and solutions
            so they can thrive and impact the governance issues they seek to
            address. During the two-year initial period, we will target specific
            countries to stimulate interest and measure impact. We would set a
            call for interest at specific times yearly in AIR first five years
            of operations and a mid-term impact assessment. Our
            selection/eligibility criteria will cover:
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Accordion
            expanded={expanded === "panel1"}
            onChange={handleChange("panel1")}
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Team Leadership
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"}>
                We are looking to invest and support innovators and civic-tech
                organizations whose outstanding and proximate leadership
                intersects the use of technology with a potential to transform
                the way we approach complex social challenges, and ability to
                create lasting change at the national and subnational level.
                This includes working with leaders who embody empathy,
                proximity, and intelligence to drive innovation in focus
                countries.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel2"}
            onChange={handleChange("panel2")}
          >
            <AccordionSummary
              aria-controls="panel2d-content"
              id="panel2d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Originality of Idea/Solution
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"}>
                A scrutiny of the ideas and solutions will be done to ascertain
                their authenticity. The registration status of ideas’ creators
                is also critical to establish the nature of the entities as well
                as testing the innovative solutions and the ability of the team
                to execute. Background checks will also be made on applications
                from individuals.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel3"}
            onChange={handleChange("panel3")}
          >
            <AccordionSummary
              aria-controls="panel3d-content"
              id="panel3d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Contextuality, Replicability, Adaptability, and Scalability
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"}>
                These metrics will be used to assess the ideas’ potential for
                growth. Scalability across geographies and pathways for
                sustainability with potential partnerships with other ecosystem
                leaders are critical to final selections.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel4"}
            onChange={handleChange("panel4")}
          >
            <AccordionSummary
              aria-controls="panel4d-content"
              id="panel4d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Change Theories, Implementation Paths, Impact Envisioning, and
                Learning
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                We will seek out ideas with clear change and impact envisioning
                on the continent. Our selection canvas will include well-defined
                impact measurement strategies and clear pathways of change
                within the focus communities.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel5"}
            onChange={handleChange("panel5")}
          >
            <AccordionSummary
              aria-controls="panel5d-content"
              id="panel5d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Partnership Pathways and Potential Inter-stakeholder Cohesion
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                There will be clarity on pathways for partnerships and the
                mechanism for effective collaboration. We are keen to see how
                the ideas and solutions engage beyond their contexts and
                localities. We would love to see the continental and global
                connectivity they envision or perhaps have in existence.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === "panel6"}
            onChange={handleChange("panel6")}
          >
            <AccordionSummary
              aria-controls="panel6d-content"
              id="panel6d-header"
            >
              <Typography color={"#fff"} fontWeight={700}>
                Financing and Other Resource Management Projections
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color={"#000"} fontWeight={400}>
                We consider this as critical in grasping applicants’ measure of
                understanding the management of critical resources relevant to
                realizing their ideas and solutions.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Box>
  );
}
